import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Crown, Mail, Split, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Left-column editor panel for the Builder. Owns:
 * - Content card (topic, title, badge, headline, subheadline, CTA, bullets, image/hero video)
 * - Form fields toggles
 * - Colors
 * - Pro panels (branded URL, DOI, A/B)
 *
 * Pure presentational — all state lifted to <Builder>.
 */
export default function BuilderEditorPanel({
  lm,
  isPro,
  variantTab,
  activeLanding,
  topic,
  setTopic,
  setLm,
  updateLanding,
  updateForm,
  aiLoading,
  aiGenerate,
  customPathInput,
  setCustomPathInput,
  saveCustomPath,
}) {
  return (
    <div className="lg:col-span-5 space-y-5">
      <ContentCard
        lm={lm}
        activeLanding={activeLanding}
        topic={topic}
        setTopic={setTopic}
        setLm={setLm}
        updateLanding={updateLanding}
        aiLoading={aiLoading}
        aiGenerate={aiGenerate}
      />
      <FormFieldsCard lm={lm} updateForm={updateForm} />
      <SenderIdentityCard lm={lm} setLm={setLm} />
      <ColorsCard lm={lm} setLm={setLm} />
      <ProPanelsCard
        lm={lm}
        setLm={setLm}
        isPro={isPro}
        customPathInput={customPathInput}
        setCustomPathInput={setCustomPathInput}
        saveCustomPath={saveCustomPath}
      />
    </div>
  );
}

function ContentCard({ lm, activeLanding, topic, setTopic, setLm, updateLanding, aiLoading, aiGenerate }) {
  return (
    <div className="card-brutal">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-bold text-lg">
          Content {lm.ab_enabled && <span className="ml-2 text-xs label-cap">Variant</span>}
        </div>
        <button onClick={aiGenerate} disabled={aiLoading} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="ai-generate-btn">
          <Sparkles size={14} /> {aiLoading ? "Generating…" : "AI Copy"}
        </button>
      </div>
      <div className="space-y-4">
        <Field label="Topic / niche (used by AI)" value={topic} onChange={setTopic} testid="builder-topic" />
        <Field label="Title (internal)" value={lm.title} onChange={(v) => setLm({ ...lm, title: v })} testid="builder-internal-title" />
        <Field label="Badge" value={activeLanding.badge} onChange={(v) => updateLanding({ badge: v })} testid="builder-badge" />
        <Field label="Headline" value={activeLanding.headline} onChange={(v) => updateLanding({ headline: v })} testid="builder-headline" />
        <Textarea label="Subheadline" value={activeLanding.subheadline} onChange={(v) => updateLanding({ subheadline: v })} testid="builder-subheadline" />
        <Field label="CTA Button Text" value={activeLanding.cta_text} onChange={(v) => updateLanding({ cta_text: v })} testid="builder-cta" />
        <div>
          <label className="label-cap block mb-2">Bullets (one per line)</label>
          <textarea
            value={(activeLanding.bullets || []).join("\n")}
            onChange={(e) => updateLanding({ bullets: e.target.value.split("\n").filter(Boolean) })}
            rows={4}
            className="input-brutal"
            data-testid="builder-bullets"
          />
        </div>
        <Field label="Image URL (optional)" value={activeLanding.image_url} onChange={(v) => updateLanding({ image_url: v })} testid="builder-image" />
        <Field
          label="Hero video URL (optional — YouTube, Vimeo, or MP4)"
          value={activeLanding.hero_video_url || ""}
          onChange={(v) => updateLanding({ hero_video_url: v })}
          testid="builder-hero-video"
        />
        <div className="text-xs text-zinc-500 -mt-2">If set, the video replaces the hero image on the landing page.</div>
      </div>
    </div>
  );
}

function FormFieldsCard({ lm, updateForm }) {
  return (
    <div className="card-brutal">
      <div className="font-display font-bold text-lg mb-4">Form fields</div>
      <div className="text-xs text-zinc-500 mb-3">Email is always required. Shared across variants.</div>
      <div className="space-y-2">
        <Toggle
          label="Show Name field"
          checked={!!lm.landing_config?.form_fields?.name_enabled}
          onChange={(v) => updateForm({ name_enabled: v })}
          testid="toggle-name"
        />
        <Toggle
          label="Show Phone field"
          checked={!!lm.landing_config?.form_fields?.phone_enabled}
          onChange={(v) => updateForm({ phone_enabled: v })}
          testid="toggle-phone"
        />
      </div>
    </div>
  );
}

function ColorsCard({ lm, setLm }) {
  const setColor = (key) => (v) =>
    setLm((p) => ({ ...p, landing_config: { ...p.landing_config, [key]: v } }));
  return (
    <div className="card-brutal">
      <div className="font-display font-bold text-lg mb-4">Colors</div>
      <div className="grid grid-cols-3 gap-3">
        <ColorField label="BG" value={lm.landing_config.bg_color} onChange={setColor("bg_color")} />
        <ColorField label="Text" value={lm.landing_config.text_color} onChange={setColor("text_color")} />
        <ColorField label="Accent" value={lm.landing_config.accent_color} onChange={setColor("accent_color")} />
      </div>
    </div>
  );
}

function SenderIdentityCard({ lm, setLm }) {
  const set = (key) => (v) => setLm((p) => ({ ...p, [key]: v }));
  return (
    <div className="card-brutal">
      <div className="font-display font-bold text-lg mb-1">Sender identity</div>
      <div className="text-xs text-zinc-500 mb-4">
        How delivery emails appear in your leads' inboxes. Both fields are optional &mdash; sensible defaults are used when blank.
      </div>
      <div className="space-y-4">
        <Field
          label='From name (e.g. "Rob from VibeLead")'
          value={lm.from_name}
          onChange={set("from_name")}
          testid="builder-from-name"
        />
        <Field
          label="Reply-to email (where lead replies go)"
          value={lm.reply_to}
          onChange={set("reply_to")}
          testid="builder-reply-to"
        />
        <div className="text-xs text-zinc-500 -mt-2">
          Tip: a real reply-to address consistently lifts open + reply rates. Use one you actually monitor.
        </div>
      </div>
    </div>
  );
}

function ProPanelsCard({ lm, setLm, isPro, customPathInput, setCustomPathInput, saveCustomPath }) {
  const onDoiChange = isPro
    ? (v) => setLm({ ...lm, require_double_optin: v })
    : () => toast.error("Pro feature");

  const onAbChange = isPro
    ? (v) =>
        setLm({
          ...lm,
          ab_enabled: v,
          variant_b_landing_config:
            v && !lm.variant_b_landing_config
              ? { ...lm.landing_config, headline: lm.landing_config.headline + " (B)" }
              : lm.variant_b_landing_config,
        })
    : () => toast.error("Pro feature");

  return (
    <div className="card-brutal border-[#FF4F00]" data-testid="pro-panels">
      <div className="flex items-center gap-2 mb-4">
        <Crown size={18} className="text-[#FF4F00]" />
        <div className="font-display font-bold text-lg">Pro features</div>
        {!isPro && <Link to="/pricing" className="ml-auto text-xs font-bold underline text-[#FF4F00]">Upgrade →</Link>}
      </div>

      {/* Branded URL */}
      <div className="mb-5 pb-5 border-b border-zinc-200">
        <div className="label-cap mb-2 flex items-center gap-2"><ExternalLink size={12} /> Branded URL</div>
        <div className="flex gap-2">
          <span className="px-3 py-3 border-2 border-black bg-zinc-50 text-sm text-zinc-500 hidden sm:inline-flex items-center">/p/</span>
          <input
            value={customPathInput}
            onChange={(e) => setCustomPathInput(e.target.value.toLowerCase())}
            disabled={!isPro}
            className="input-brutal flex-1"
            placeholder="your-brand"
            data-testid="custom-path-input"
          />
          <button onClick={saveCustomPath} disabled={!isPro} className="btn-brutal !px-4 !py-2 text-sm" data-testid="save-custom-path-btn">Set</button>
        </div>
        <div className="text-xs text-zinc-500 mt-2">3-30 chars: lowercase letters, digits, dashes. Default URL still works.</div>
      </div>

      {/* DOI toggle */}
      <div className="mb-5 pb-5 border-b border-zinc-200">
        <Toggle
          label={<span className="flex items-center gap-2"><Mail size={14} /> Require email confirmation (double opt-in)</span>}
          checked={!!lm.require_double_optin}
          onChange={onDoiChange}
          testid="toggle-doi"
        />
        <div className="text-xs text-zinc-500 mt-2">Stronger GDPR compliance. Visitors confirm via email link before getting content.</div>
      </div>

      {/* A/B testing toggle */}
      <div>
        <Toggle
          label={<span className="flex items-center gap-2"><Split size={14} /> Enable A/B testing (50/50 split)</span>}
          checked={!!lm.ab_enabled}
          onChange={onAbChange}
          testid="toggle-ab"
        />
        <div className="text-xs text-zinc-500 mt-2">Two headlines compete. See conversion winners in Analytics.</div>
      </div>
    </div>
  );
}

// ---------- Form primitives ----------
function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-cap block mb-2">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-brutal" data-testid={testid} />
    </div>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-cap block mb-2">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="input-brutal" data-testid={testid} />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 py-2 border-2 border-black cursor-pointer" data-testid={testid}>
      <span className="text-sm font-bold">{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-[#FF4F00]" />
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className="label-cap block mb-2">{label}</label>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 border-2 border-black cursor-pointer" />
    </div>
  );
}
